import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from '../i18n';
import { tokenExceptions } from '../constans/api';
import { genToastBody, getErrorDesc, getStorageItem } from '../helpers/helpers';
import { IJWTTokenResp, refreshTokenRequest } from '../services/temporary';

type TRefreshCallback = (token: string) => void;

export const TEMPORARY_TOKENS_KEY = 'temporary:tokens';

const VITE_APP_API_URL = import.meta.env.VITE_APP_API_URL;

let requestQueue: TRefreshCallback[] = [];
let isRefreshInProgress = false;

const instance = axios.create({ baseURL: VITE_APP_API_URL });

const initRefresh = (token: string) => {
  requestQueue.map((callback: TRefreshCallback) => callback(token));
};

const subscribeTokenRefresh = (callback: TRefreshCallback) => {
  requestQueue.push(callback);
};

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const { headers, url } = config;

    if (headers) {
      if (url) {
        headers['Accept-Language'] = i18n.language;

        const data = getStorageItem<IJWTTokenResp>(TEMPORARY_TOKENS_KEY, 'session');

        if (data?.access && !tokenExceptions.includes(url)) {
          headers['Authorization'] = `Bearer ${data?.access}`;
        }

        return config;
      }

      return config;
    }

    return config;
  },
  async (error: AxiosError) => {
    const { response } = error;

    return await Promise.reject(response);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const { config, response } = error;
    const { data, status } = response as AxiosResponse;

    const details = getErrorDesc(data);

    const { description, title } = details;

    if (status === 401) {
      const retry = new Promise(resolve => {
        const originalRequest = config;

        subscribeTokenRefresh((token: string) => {
          const { headers } = originalRequest;

          if (headers) {
            headers['Authorization'] = `Bearer ${token}`;

            resolve(axios(originalRequest));
          }
        });
      });

      if (!isRefreshInProgress) {
        isRefreshInProgress = true;

        const { access } = (await refreshTokenRequest()) ?? {};

        isRefreshInProgress = false;

        initRefresh(access ? access : '');
        requestQueue = [];
      }

      return retry;
    }

    await genToastBody(title, description);

    return Promise.reject(response);
  }
);

export default instance;
